import { isTrustedGhlMessage, postToGhl } from '../utils/ghlMessaging';

/* The above class is a JavaScript GHL helper class that retrieves user data by sending a request to a server and
decrypting the response using a key. */
export class GHL {
  appId;

  constructor() {}

  /**
   * 對 window.parent 送出 REQUEST_USER_DATA，等待 GHL 回應 REQUEST_USER_DATA_RESPONSE，
   * 回傳其中仍為加密狀態的 key。getUserData()（送 /decrypt-sso 解密）與 requestSsoKey()
   * （R5 票 06，原封交給後端解密）共用同一段握手機制，避免兩份逾時／清理邏輯各自維護後
   * 悄悄長歪（同一份 drift 疑慮，ghlMessaging.js 的 startReadyResendLoop 已有過前例）。
   * 是否在 iframe／預覽模式中呼叫由各自呼叫端自行判斷，這裡不重複檢查。
   */
  _requestRawSsoKey() {
    return new Promise((resolve, reject) => {
      let timeout;

      const messageHandler = (event) => {
        if (!isTrustedGhlMessage(event)) return;
        const { data } = event;
        console.log("Received message:", data);
        if (data.message === "REQUEST_USER_DATA_RESPONSE") {
          clearTimeout(timeout);
          window.removeEventListener("message", messageHandler);
          resolve(data.payload);
        }
      };

      window.addEventListener("message", messageHandler);
      console.log("Sending REQUEST_USER_DATA...");
      postToGhl(window.parent, { message: "REQUEST_USER_DATA" });

      // 設定 3 秒超時
      timeout = setTimeout(() => {
        window.removeEventListener("message", messageHandler);
        console.log("Timeout waiting for user data response");
        reject(new Error("Timeout waiting for user data response"));
      }, 3000);
    });
  }

  async getUserData() {
    try {
      console.log("Starting getUserData...");
      const inIframe = window.parent !== window;

      // 檢查是否在 marketplace 預覽模式
      const isPreviewMode = window.location.href.includes('marketplace.gohighlevel.com') ||
                           window.location.href.includes('app-settings');

      if (isPreviewMode) {
        console.log("Preview mode detected, returning mock data");
        return {
          locationId: 'preview-location-id',
          companyId: 'preview-company-id',
          userId: 'preview-user-id',
          isPreview: true
        };
      }

      // 若不在 iframe 之中，代表不是在 GHL 付款連結/嵌入環境，無法透過 postMessage 取得 user data
      if (!inIframe) {
        console.log("Not running inside an iframe; skipping REQUEST_USER_DATA flow");
        throw new Error('Not in GHL iframe environment');
      }

      const key = await this._requestRawSsoKey();

      if (!key) {
        throw new Error("No user data key received");
      }

      console.log("Got key, fetching from server...");
      const res = await fetch('/decrypt-sso', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ key })
      });
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const data = await res.json();
      console.log("Received data from server:", data);
      
      if (!data || typeof data !== 'object') {
        throw new Error("Invalid user data format");
      }
      
      // 如果有 activeLocation 但沒有 locationId，使用 activeLocation 作為 locationId
      if (data.activeLocation && !data.locationId) {
        data.locationId = data.activeLocation;
        console.log("Using activeLocation as locationId:", data.locationId);
      }
      
      return data;
    } catch (error) {
      console.error("Error getting user data:", error);
      
      // 在開發環境或預覽模式中返回模擬資料
      if (process.env.NODE_ENV === 'development' || 
          window.location.href.includes('marketplace') ||
          window.location.href.includes('localhost')) {
        console.log("Returning development mock data");
        return {
          locationId: 'dev-location-id',
          companyId: 'dev-company-id',
          userId: 'dev-user-id',
          isDevelopment: true
        };
      }
      throw error;
    }
  }

  /**
   * R5 票 06：取出「未解密」的 Custom Page SSO key，直接交給後端 `/api/payment-method/
   * operator/cards`／`.../:id/delete` 自行解密驗證（GHL.resolveOperatorLocationId），
   * 而不是像 getUserData() 一樣先送 /decrypt-sso 解密再把結果交給瀏覽器——操作人員刪卡
   * 分頁需要的是「locationId 由伺服器端驗證」，若把已解密的資料交回瀏覽器，locationId
   * 就變成瀏覽器可宣稱的欄位，不再是可信邊界。不重用 getUserData() 的預覽／開發模式 mock
   * fallback：這裡沒有對應的卡片列表可以假造，失敗就該直接讓呼叫端知道拿不到 key。
   */
  async requestSsoKey() {
    const inIframe = window.parent !== window;
    if (!inIframe) {
      throw new Error('Not in GHL iframe environment');
    }

    const key = await this._requestRawSsoKey();

    if (!key) {
      throw new Error("No SSO key received");
    }

    return key;
  }
}
